import type { RefCallback } from 'react';
import { scheduleDrawingThumbnail } from '../services/drawingThumbnailQueue';

type LoadThumbnail = (url: string, signal: AbortSignal) => Promise<Blob>;
type Entry = { controller: AbortController; promise: Promise<string>; url?: string; bytes: number };

// Memory only: no HTTP/disk cache, and never shared across account/permission
// scopes. The drawing queue already supplies cancellation and a limit of three.
export class ThumbnailCache {
  private entries = new Map<string, Entry>();
  private bytes = 0;

  constructor(private maxEntries = 200, private maxBytes = 32 * 1024 * 1024) {}

  load(scope: string, url: string, load: LoadThumbnail): Promise<string> {
    const key = JSON.stringify([scope, url]);
    const existing = this.entries.get(key);
    if (existing) {
      this.entries.delete(key);
      this.entries.set(key, existing);
      return existing.promise;
    }
    const controller = new AbortController();
    const entry: Entry = { controller, bytes: 0, promise: Promise.resolve('') };
    entry.promise = scheduleDrawingThumbnail(() => load(url, controller.signal), controller.signal)
      .then(blob => {
        if (controller.signal.aborted) throw new DOMException('Thumbnail cancelled', 'AbortError');
        if (!blob.type.startsWith('image/') || !blob.size || blob.size > Math.min(this.maxBytes, 2 * 1024 * 1024)) {
          throw new Error('Invalid report thumbnail');
        }
        entry.url = URL.createObjectURL(blob);
        entry.bytes = blob.size;
        this.bytes += blob.size;
        // Touch on completion as well: a slow older request is the newest
        // usable image. Bound both the entry count and actual blob bytes.
        this.entries.delete(key);
        this.entries.set(key, entry);
        this.trim();
        return entry.url;
      }).catch(error => {
        if (this.entries.get(key) === entry) this.remove(key, entry);
        throw error;
      });
    this.entries.set(key, entry);
    this.trim();
    return entry.promise;
  }

  private trim() {
    while (this.entries.size > this.maxEntries || this.bytes > this.maxBytes) {
      const oldest = this.entries.entries().next().value as [string, Entry] | undefined;
      if (!oldest) break;
      this.remove(...oldest);
    }
  }

  private remove(key: string, entry: Entry) {
    this.entries.delete(key);
    entry.controller.abort();
    this.bytes -= entry.bytes;
    if (entry.url) URL.revokeObjectURL(entry.url);
  }

  clear() {
    for (const [key, entry] of this.entries) this.remove(key, entry);
  }
}

const sessionCache = new ThumbnailCache();
export function clearThumbnailCache() { sessionCache.clear(); }

// Keep ref identities stable across report state updates (upload progress,
// notes, etc.). Drop callbacks when their row leaves the rendered gallery.
export function createThumbnailRefs(scope: string, load: LoadThumbnail) {
  const refs = new Map<string, RefCallback<HTMLImageElement>>();
  return (url: string): RefCallback<HTMLImageElement> => {
    const existing = refs.get(url);
    if (existing) return existing;
    const bind = lazyThumbnailRef(scope, url, load);
    const ref: RefCallback<HTMLImageElement> = element => {
      bind(element);
      if (!element) refs.delete(url);
    };
    refs.set(url, ref);
    return ref;
  };
}

// Attach to the existing <img>. Detached rows stop observing/updating, while
// completed fetches survive navigation in RAM until eviction or session cleanup.
export function lazyThumbnailRef(scope: string, url: string, load: LoadThumbnail): RefCallback<HTMLImageElement> {
  let cleanup: (() => void) | undefined;
  return element => {
    cleanup?.();
    cleanup = undefined;
    if (!element) return;
    element.removeAttribute('src');
    delete element.dataset.fallbackApplied;
    let cancelled = false;
    let started = false;
    let observer: IntersectionObserver | undefined;
    const start = () => {
      if (started || cancelled) return;
      started = true;
      observer?.disconnect();
      void sessionCache.load(scope, url, load).then(objectUrl => {
        if (!cancelled) element.src = objectUrl;
      }).catch(error => {
        if (!cancelled && error?.name !== 'AbortError') element.dispatchEvent(new Event('error'));
      });
    };
    if (typeof IntersectionObserver === 'undefined') start();
    else {
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) start();
      }, { rootMargin: '150px' });
      observer.observe(element);
    }
    cleanup = () => { cancelled = true; observer?.disconnect(); };
  };
}
